import _ from "lodash";
import React, { CSSProperties, useCallback, useEffect, useRef, useState } from "react";
import { RegisterOptions } from "react-hook-form";
import {
  ImageStyle,
  StyleProp,
  StyleSheet,
  Text,
  TextInput,
  TextInputProps,
  TextProps,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from "react-native";
import { useSelector } from "react-redux";
import { useAppDispatch } from "src/redux";
import { kColors } from "src/kColors";
import { kFonts } from "src/kFonts";
import { kStyleConst } from "src/kStyle";
import { stringNotEmpty } from "src/util/gFormatting";
import { tOption } from "tmt_api/src/types/types.validator";
import { tDispatchAction, tReduxState } from "../redux/types";
import ComboBox from "./ComboBox";
import { CancelIcon, Import_Sm } from "./impComponents";
import { CheckMark2 } from "./NewCheckBox2";
import { SLImage } from "./ReactiveImage";
import ShimmerView from "./ShimmerView";
import { YesNo } from "./YesNo";

export type tValidatedTitleProps = {
  myTitle: string;
  rules?: { required?: { message: string }; pattern?: { pattern: RegExp; message: string } };
  textProps: TextProps;
  myViewStyle?: ViewStyle | ViewStyle[];
  setController: React.Dispatch<React.SetStateAction<tValidType>>;
  controller: { [key: string]: boolean };
  value: string;
  uniqueName: string;
  isShown?: boolean;
};
export type tValidType = { [key: string]: boolean };
export type tpValidatedTitle = (props: tValidatedTitleProps) => JSX.Element;
export const ValidatedTitle: tpValidatedTitle = ({
  isShown,
  myTitle,
  textProps,
  rules,
  uniqueName,
  setController,
  value,
  myViewStyle = styles.row,
}) => {
  const [requiredCheck, setReq] = useState(true);
  const [patternCheck, setPat] = useState(true);
  const aPatternPattern = rules?.pattern?.pattern;
  const aRequired = rules?.required;
  const aPattern = rules?.pattern;

  useEffect(() => {
    setController((previousValue) => {
      return { ...previousValue, [uniqueName]: patternCheck && requiredCheck };
    });
  }, [patternCheck, requiredCheck, setController, uniqueName]);

  useEffect(() => {
    if (value !== "" || aRequired === undefined) {
      setReq(true);
    } else {
      setReq(false);
    }
    if ((aPatternPattern && value.match(aPatternPattern) !== null) || !aPattern || (value === "" && !aRequired)) {
      setPat(true);
    } else {
      setPat(false);
    }
  }, [value, aPatternPattern, aRequired, aPattern, setReq, setPat]);

  return (
    <View style={myViewStyle}>
      <Text {...textProps}>{myTitle}</Text>
      {!requiredCheck && isShown && <Text style={styles.inputErrorLabel}>{rules?.required?.message}</Text>}
      {!requiredCheck && !patternCheck && isShown && <Text style={styles.inputErrorLabelNoPadding}>, </Text>}
      {!patternCheck && isShown && <Text style={styles.inputErrorLabelNoPadding}>{rules?.pattern?.message}</Text>}
    </View>
  );
};

export type tValidatedTextFormProps<T extends string | undefined> = {
  uniqueName: string;
  myTitle: string;
  loading?: boolean;
  rules?: { required?: { message: string }; pattern?: { pattern: RegExp; message: string } };
  titleViewStyle?: ViewStyle | ViewStyle[];
  shimmerStyle?: StyleProp<ViewStyle>;
  textInputProps: TextInputProps;
  setController: React.Dispatch<React.SetStateAction<tValidType>>;
  controller: { [key: string]: boolean };
  wrapperStyle?: StyleProp<ViewStyle>;
  isShown?: (state: tReduxState) => boolean;
  dispatchAction: tDispatchAction;
  dispatchPath: T;
  titleProps?: TextProps;
  selectorFunction: (state: tReduxState) => string | undefined;
};
export const ValidatedTextFormInput = <T extends string | undefined>({
  myTitle,
  textInputProps,
  loading,
  titleViewStyle,
  rules,
  controller,
  uniqueName,
  selectorFunction,
  dispatchAction,
  setController,
  dispatchPath = "",
  wrapperStyle,
  titleProps = { style: styles.P09_FormTitle },
  isShown = () => false,
}: tValidatedTextFormProps<T>): JSX.Element => {
  const dispatch = useAppDispatch();
  const rValue = useSelector(selectorFunction);
  const rIsShown = useSelector(isShown);
  const _dispatchHandler = useCallback(
    (myText: string) => dispatch(_.set(dispatchAction, dispatchPath, myText)),
    [dispatch, dispatchAction, dispatchPath],
  );
  const [requiredCheck, setReq] = useState(true);
  const [patternCheck, setPat] = useState(true);
  const textRef = useRef(null);
  const aTextValue = textInputProps.value;
  const aPatternPattern = rules?.pattern?.pattern;
  const aRequired = rules?.required;
  const aPattern = rules?.pattern;

  useEffect(() => {
    setController((previousValue) => {
      return { ...previousValue, [uniqueName]: patternCheck && requiredCheck };
    });
    return undefined;
  }, [patternCheck, requiredCheck, setController, uniqueName]);

  useEffect(() => {
    if (aTextValue !== "" || aRequired === undefined) {
      setReq(true);
    } else {
      setReq(false);
    }
    if (
      (aPatternPattern && aTextValue?.match(aPatternPattern) !== null) ||
      !aPattern ||
      (aTextValue === "" && !aRequired)
    ) {
      setPat(true);
    } else {
      setPat(false);
    }
  }, [aTextValue, aPatternPattern, aRequired, aPattern]);

  return (
    <View style={wrapperStyle}>
      <ValidatedTitle
        myViewStyle={titleViewStyle}
        controller={controller}
        myTitle={myTitle}
        setController={setController}
        textProps={titleProps}
        uniqueName={uniqueName}
        value={rValue ?? ""}
        rules={rules}
        isShown={rIsShown}
      />
      <ShimmerView loading={loading}>
        <TextInput ref={textRef} value={rValue} onChangeText={_dispatchHandler} {...textInputProps} />
      </ShimmerView>
    </View>
  );
};

export type tFormTextProps<T extends string | undefined> = {
  myTitle: string;
  loading?: boolean;
  shimmerStyle?: StyleProp<ViewStyle>;
  textInputProps: TextInputProps;
  wrapperStyle?: ViewStyle;
  titleStyle?: TextStyle;
  dispatchAction: tDispatchAction;
  dispatchPath: T;
  selectorFunction: (state: tReduxState) => string | undefined;
};
export const TextFormInput = <T extends string | undefined>({
  myTitle,
  textInputProps,
  loading,
  dispatchAction,
  dispatchPath = "",
  selectorFunction,
  wrapperStyle = styles.gAlignItems,
  titleStyle = styles.P09_FormTitle,
}: tFormTextProps<T>): JSX.Element => {
  const textRef = useRef(null);
  const dispatch = useAppDispatch();
  const _dispatchHandler = useCallback(
    (myText: string) => dispatch(_.set(dispatchAction, dispatchPath, myText)),
    [dispatch, dispatchAction, dispatchPath],
  );
  const rValue = useSelector(selectorFunction);

  return (
    <View style={wrapperStyle}>
      <Text style={titleStyle}>{myTitle}</Text>
      <ShimmerView loading={loading}>
        <TextInput ref={textRef} value={rValue ?? ""} onChangeText={_dispatchHandler} {...textInputProps} />
      </ShimmerView>
    </View>
  );
};

export const ImageSelectValidated = ({
  myTitle,
  value,
  onPress,
  onPressDelete,
  loading,
  touchStyle,
  webImageStyle,
  mobileImageStyle,
}: {
  myTitle?: string;
  defaultValue?: string;
  rules?: Omit<RegisterOptions, "valueAsNumber" | "valueAsDate" | "setValueAs">;
  loading?: boolean;
  touchStyle?: StyleProp<ViewStyle>;
  webImageStyle: CSSProperties;
  mobileImageStyle: StyleProp<ImageStyle>;
  shimmerStyle?: StyleProp<ViewStyle>;
  multiline?: boolean;
  value?: string;
  onPress?: () => void;
  onPressDelete?: () => void;
}): JSX.Element => {
  return (
    <>
      <TouchableOpacity onPress={onPress} style={touchStyle}>
        <ShimmerView style={mobileImageStyle} loading={!loading}>
          {stringNotEmpty(value) ? (
            <View>
              {onPressDelete ? (
                <TouchableOpacity onPress={onPressDelete} style={styles.P09_DiscardAbsoluteHeroImages}>
                  <CancelIcon />
                </TouchableOpacity>
              ) : (
                <></>
              )}
              <SLImage
                mobileStyle={[mobileImageStyle, styles.zIndexTen]}
                webStyle={{ ...webImageStyle, ...webStyles.zIndexTen }}
                source={{ uri: value }}
              />
            </View>
          ) : (
            <View style={touchStyle}>
              <Import_Sm style={styles.P09_LegendStamp} />
              <Text>{myTitle}</Text>
            </View>
          )}
        </ShimmerView>
      </TouchableOpacity>
    </>
  );
};

export type tValidatedComboBoxProps<T extends string | undefined> = {
  uniqueName?: string;
  placeholder?: string;
  autoComplete?: TextInputProps["autoCompleteType"];
  myTitle: string;
  rules?: { required?: { message: string } };
  loading?: boolean;
  shimmerStyle?: StyleProp<ViewStyle>;
  controller: { [key: string]: boolean };
  wrapperStyle?: ViewStyle | ViewStyle[];
  titleViewStyle?: ViewStyle | ViewStyle[];
  comboBoxStyle?: StyleProp<ViewStyle>;
  titleProps?: TextProps;
  dispatchAction: tDispatchAction;
  dispatchPath: T;
  searchable?: boolean;
  ComboOptions: tOption<string>[];
  normalizerFunction?: ((val: string) => string) | undefined;
  setController: React.Dispatch<React.SetStateAction<tValidType>>;
  isShown?: (state: tReduxState) => boolean;
  selectorFunction: (state: tReduxState) => string | undefined;
};
export const ValidatedComboBoxInput = <T extends string | undefined>({
  myTitle,
  placeholder,
  loading,
  rules,
  autoComplete,
  searchable,
  normalizerFunction,
  titleViewStyle,
  controller,
  dispatchAction,
  dispatchPath = "",
  uniqueName = "",
  selectorFunction,
  setController,
  ComboOptions,
  wrapperStyle = styles.gAlignItems,
  comboBoxStyle = styles.applyComboBoxView2,
  titleProps = { style: styles.P09_FormTitle },
  isShown = () => false,
}: tValidatedComboBoxProps<T>): JSX.Element => {
  const [requiredCheck, setReq] = useState(true);
  const aRequired = rules?.required;
  const dispatch = useAppDispatch();
  const rValue = useSelector(selectorFunction);
  const rIsShown = useSelector(isShown);
  const _dispatchHandler = useCallback(
    (myOption: tOption<string>) => dispatch(_.set(dispatchAction, dispatchPath, myOption.myValue)),
    [dispatch, dispatchAction, dispatchPath],
  );

  useEffect(() => {
    setController((previousValue) => {
      return { ...previousValue, [uniqueName]: requiredCheck };
    });
    return undefined;
  }, [requiredCheck, setController, uniqueName]);

  useEffect(() => {
    if (rValue !== "" || aRequired === undefined) {
      setReq(true);
    } else {
      setReq(false);
    }
  }, [aRequired, rValue]);

  return (
    <View style={wrapperStyle}>
      <ValidatedTitle
        myViewStyle={titleViewStyle}
        controller={controller}
        myTitle={myTitle}
        setController={setController}
        textProps={titleProps}
        uniqueName={uniqueName}
        value={rValue ?? ""}
        rules={rules}
        isShown={rIsShown}
      />
      <ShimmerView loading={loading}>
        <View style={comboBoxStyle}>
          <ComboBox
            disabled={searchable}
            myAutocompleteType={autoComplete}
            value={rValue}
            onSelect={_dispatchHandler}
            myOptions={ComboOptions}
            myPlaceholder={placeholder}
            myNormalizeHandler={normalizerFunction}
          />
        </View>
      </ShimmerView>
    </View>
  );
};
export type tFormComboBoxProps<T extends string | undefined> = {
  myTitle: string;
  loading?: boolean;
  autoComplete?: TextInputProps["autoCompleteType"];
  shimmerStyle?: StyleProp<ViewStyle>;
  wrapperStyle?: ViewStyle | ViewStyle[];
  comboBoxStyle?: ViewStyle;
  titleStyle?: TextStyle;
  placeholder?: string;
  ComboOptions: tOption<string>[];
  searchable?: boolean;
  normalizerFunction?: ((val: string) => string) | undefined;
  // onSelectChange: (myOption: tOption<string>, myIndex: number) => void;
  selectorFunction: (state: tReduxState) => string | undefined;
  dispatchAction: tDispatchAction;
  dispatchPath: T;
};
export const FormComboBoxInput = <T extends string | undefined>({
  myTitle,
  placeholder,
  loading,
  autoComplete,
  normalizerFunction,
  dispatchPath = "",
  dispatchAction,
  searchable,
  // onSelectChange,
  selectorFunction,
  ComboOptions,
  comboBoxStyle = styles.applyComboBoxView2,
  wrapperStyle,
  titleStyle,
}: tFormComboBoxProps<T>): JSX.Element => {
  const dispatch = useAppDispatch();
  const _dispatchHandler = useCallback(
    (myOption: tOption<string>) => dispatch(_.set(dispatchAction, dispatchPath, myOption.myValue)),
    [dispatch, dispatchAction, dispatchPath],
  );
  const rValue = useSelector(selectorFunction);

  return (
    <View style={wrapperStyle}>
      <Text style={titleStyle}>{myTitle}</Text>
      <ShimmerView loading={loading}>
        <View style={comboBoxStyle}>
          <ComboBox
            disabled={searchable}
            myAutocompleteType={autoComplete}
            value={rValue}
            onSelect={_dispatchHandler}
            myOptions={ComboOptions}
            myPlaceholder={placeholder}
            myNormalizeHandler={normalizerFunction}
          />
        </View>
      </ShimmerView>
    </View>
  );
};

export type tValidatedYesNoButtonsProps = {
  uniqueName: string;
  myTitle: string;
  rules?: { required?: { message: string }; pattern?: { pattern: RegExp; message: string } };
  loading?: boolean;
  shimmerStyle?: StyleProp<ViewStyle>;
  setController: React.Dispatch<React.SetStateAction<tValidType>>;
  controller: { [key: string]: boolean };
  wrapperStyle?: ViewStyle;
  titleViewStyle?: ViewStyle;
  isShown?: (state: tReduxState) => boolean;
  dispatchActionTrue: tDispatchAction;
  dispatchActionFalse: tDispatchAction;
  titleProps?: TextProps;

  selectorFunction: (state: tReduxState) => boolean | undefined;
};
export type tpValidatedYesNoButtons = (props: tValidatedYesNoButtonsProps) => JSX.Element;
export const ValidatedYesNoButtons: tpValidatedYesNoButtons = ({
  myTitle,
  loading,
  rules,
  controller,
  uniqueName,
  titleViewStyle,
  wrapperStyle = styles.gAlignItems,
  titleProps = { style: styles.P09_FormTitle },
  dispatchActionTrue,
  dispatchActionFalse,
  selectorFunction,
  setController,
  isShown = () => false,
}) => {
  const dispatch = useAppDispatch();
  const _dispatchHandlerFalse = useCallback(() => dispatch(dispatchActionFalse), [dispatch, dispatchActionFalse]);
  const _dispatchHandlerTrue = useCallback(() => dispatch(dispatchActionTrue), [dispatch, dispatchActionTrue]);

  const [requiredCheck, setReq] = useState(true);
  const aRequired = rules?.required;
  const rValue = useSelector(selectorFunction);
  const rIsShown = useSelector(isShown);

  useEffect(() => {
    setController((previousValue) => {
      return { ...previousValue, [uniqueName]: requiredCheck };
    });
    return undefined;
  }, [requiredCheck, setController, uniqueName]);

  useEffect(() => {
    if (rValue !== undefined || aRequired === undefined) {
      setReq(true);
    } else {
      setReq(false);
    }
  }, [aRequired, rValue]);

  return (
    <View style={wrapperStyle}>
      <ValidatedTitle
        myViewStyle={titleViewStyle}
        controller={controller}
        myTitle={myTitle}
        setController={setController}
        textProps={titleProps}
        uniqueName={uniqueName}
        value={rValue === undefined ? "" : "right"}
        rules={rules}
        isShown={rIsShown}
      />
      <ShimmerView loading={loading}>
        <YesNo
          myState={rValue ?? undefined}
          onPressTrue={_dispatchHandlerTrue}
          onPressFalse={_dispatchHandlerFalse}
          myText={["No", "Yes"]}
        />
      </ShimmerView>
    </View>
  );
};
export type tYesNoFormButtonsProps = {
  myTitle: string;
  loading?: boolean;
  shimmerStyle?: StyleProp<ViewStyle>;
  wrapperStyle?: ViewStyle;
  dispatchActionTrue: tDispatchAction;
  dispatchActionFalse: tDispatchAction;
  titleStyle?: TextStyle;

  selectorFunction: (state: tReduxState) => boolean | undefined;
};
export type tpYesNoFormButtons = (props: tYesNoFormButtonsProps) => JSX.Element;
export const YesNoFormButtons: tpYesNoFormButtons = ({
  myTitle,
  loading,
  wrapperStyle,
  titleStyle = styles.P09_FormTitle,
  dispatchActionTrue,
  dispatchActionFalse,
  selectorFunction,
}) => {
  const dispatch = useAppDispatch();
  const _dispatchHandlerFalse = useCallback(() => dispatch(dispatchActionFalse), [dispatch, dispatchActionFalse]);
  const _dispatchHandlerTrue = useCallback(() => dispatch(dispatchActionTrue), [dispatch, dispatchActionTrue]);

  const rValue = useSelector(selectorFunction);

  return (
    <View style={wrapperStyle}>
      <Text style={titleStyle}>{myTitle}</Text>
      <ShimmerView loading={loading}>
        <YesNo
          myState={rValue ?? undefined}
          onPressTrue={_dispatchHandlerTrue}
          onPressFalse={_dispatchHandlerFalse}
          myText={["No", "Yes"]}
        />
      </ShimmerView>
    </View>
  );
};
export type tValidatedCheckMarkProps = {
  uniqueName: string;
  myTitle: string;
  rules?: { required?: { message: string }; pattern?: { pattern: RegExp; message: string } };
  loading?: boolean;
  shimmerStyle?: StyleProp<ViewStyle>;
  setController: React.Dispatch<React.SetStateAction<tValidType>>;
  controller: { [key: string]: boolean };
  wrapperStyle?: ViewStyle;
  titleViewStyle?: ViewStyle;
  isShown?: (state: tReduxState) => boolean;
  dispatchAction: tDispatchAction;
  titleProps?: TextProps;
  buttonText?: string;
  buttonTextStyle?: TextStyle;
  buttonTextPosition?: "left" | "right" | undefined;
  selectorFunction: (state: tReduxState) => boolean | undefined;
};
export type tpValidatedCheckMark = (props: tValidatedCheckMarkProps) => JSX.Element;
export const ValidatedCheckMark: tpValidatedCheckMark = ({
  myTitle,
  loading,
  rules,
  controller,
  uniqueName,
  titleViewStyle,
  wrapperStyle = styles.gAlignItems,
  titleProps = styles.P09_FormTitle,
  dispatchAction,
  selectorFunction,
  buttonTextStyle,
  buttonTextPosition,
  buttonText,
  setController,
  isShown = () => false,
}) => {
  const dispatch = useAppDispatch();
  const _dispatchHandler = useCallback(() => dispatch(dispatchAction), [dispatch, dispatchAction]);

  const [requiredCheck, setReq] = useState(true);
  const aRequired = rules?.required;
  const rValue = useSelector(selectorFunction);
  const rIsShown = useSelector(isShown);

  useEffect(() => {
    setController((previousValue) => {
      return { ...previousValue, [uniqueName]: requiredCheck };
    });
    return undefined;
  }, [requiredCheck, setController, uniqueName]);

  useEffect(() => {
    if (rValue !== undefined || aRequired === undefined) {
      setReq(true);
    } else {
      setReq(false);
    }
  }, [aRequired, rValue]);

  return (
    <View style={wrapperStyle}>
      <ValidatedTitle
        myViewStyle={titleViewStyle}
        controller={controller}
        myTitle={myTitle}
        setController={setController}
        textProps={{ style: titleProps }}
        uniqueName={uniqueName}
        value={rValue === undefined ? "" : "right"}
        rules={rules}
        isShown={rIsShown}
      />
      <ShimmerView loading={loading}>
        <CheckMark2
          myText={buttonText}
          myState={rValue}
          onPress={_dispatchHandler}
          myLeaveSpace={true}
          textPlacement={buttonTextPosition}
          textStyle={buttonTextStyle}
        />
      </ShimmerView>
    </View>
  );
};
export type tCheckMarkProps = {
  myTitle: string;
  loading?: boolean;
  shimmerStyle?: StyleProp<ViewStyle>;
  wrapperStyle?: ViewStyle;
  dispatchAction: tDispatchAction;
  titleStyle?: TextStyle;
  buttonText?: string;
  buttonTextStyle?: TextStyle;
  buttonTextPosition?: "left" | "right" | undefined;
  selectorFunction: (state: tReduxState) => boolean | undefined;
};
export type tpCheckMark = (props: tCheckMarkProps) => JSX.Element;
export const CheckMark: tpCheckMark = ({
  myTitle,
  loading,
  wrapperStyle,
  titleStyle,
  dispatchAction,
  selectorFunction,
  buttonTextStyle,
  buttonTextPosition,
  buttonText,
}) => {
  const dispatch = useAppDispatch();
  const _dispatchHandler = useCallback(() => dispatch(dispatchAction), [dispatch, dispatchAction]);
  const rValue = useSelector(selectorFunction);

  return (
    <View style={wrapperStyle}>
      <Text style={titleStyle}>{myTitle}</Text>
      <ShimmerView loading={loading}>
        <CheckMark2
          myText={buttonText}
          myState={rValue}
          onPress={_dispatchHandler}
          myLeaveSpace={true}
          textPlacement={buttonTextPosition}
          textStyle={buttonTextStyle}
        />
      </ShimmerView>
    </View>
  );
};

const styles = StyleSheet.create({
  P09_DiscardAbsoluteHeroImages: {
    ...kStyleConst.kAddImageDiscardDimensions,
    position: "absolute",
    right: kStyleConst.kSpacing,
    top: kStyleConst.kSpacing,
    zIndex: 11,
  },
  P09_FormTitle: {
    ...kFonts.formLegend,
  },
  P09_LegendStamp: {
    ...kStyleConst.kLegendStamp,
    marginHorizontal: kStyleConst.kSpacing * 2,
    marginVertical: 5,
  },
  applyComboBoxView2: {
    flexDirection: "row",
    marginTop: -3,
    paddingVertical: 2 * kStyleConst.kSpacing,
    zIndex: 10,
  },
  gAlignItems: {
    alignItems: "flex-start",
  },
  inputErrorLabel: {
    color: kColors.red,
    paddingLeft: kStyleConst.kSpacing,
    zIndex: 12,
  },
  inputErrorLabelNoPadding: {
    color: kColors.red,
    zIndex: 12,
  },
  row: {
    flexDirection: "row",
    marginTop: 3 * kStyleConst.kSpacing,
    position: "relative",
    zIndex: 1000,
  },
  zIndexTen: {
    zIndex: 10,
  },
});

const webStyles: { [key: string]: CSSProperties } = {
  zIndexTen: {
    zIndex: 10,
  },
} as const;
